import { type ReactNode, useEffect, useEffectEvent, useMemo, useRef, useState } from 'react'
import { Link } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useQuotableProducts } from '@/api/hooks'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { formatMoney } from '@/lib/utils'
import {
  sanitizeRangeInput,
  type MultiSelections,
  type PickedProduct,
  type ProductSearchState,
  type ProductSort,
  type RangeSelections,
  type ShopifyFilterGroup,
  type ShopifySearchProduct,
} from './search-state'
import {
  activeFilterCount,
  buildCompactPageItems,
  formatProductResultsCopy,
  formatProductSearchCount,
  hasActiveContextFilters,
  isRenderableFilterGroup,
  orderFilterGroups,
  pickedFromQuotable,
  pickedFromStorefront,
  selectedRange,
} from './search-filters'
import { useShopifyFilterOptions, useShopifySearch } from './search-queries'
import { FilterPanel } from './product-filters'
import { SearchInput } from '@/components/search-input'

const DEFAULT_PUBLIC_PER_PAGE = 50
const PUBLIC_PER_PAGE_OPTIONS = [24, 50, 100]
const PRODUCT_SORT_OPTIONS: Array<{ value: ProductSort; label: string }> = [
  { value: 'featured', label: 'Featured' },
  { value: 'price_desc', label: 'Price: High to Low' },
  { value: 'price_asc', label: 'Price: Low to High' },
  { value: 'relevance', label: 'Relevance' },
  { value: 'newest', label: 'Newest' },
]

function ProductPickCard({
  product,
  badge,
  onSelect,
  disabled = false,
}: {
  product: PickedProduct
  badge?: string
  onSelect?: (product: PickedProduct) => void
  disabled?: boolean
}) {
  const content = (
    <>
      <div className="aspect-square bg-muted flex items-center justify-center overflow-hidden">
        {product.thumbnail ? (
          <img
            src={product.thumbnail}
            alt={product.title}
            className="h-full w-full object-contain p-2"
          />
        ) : (
          <div className="text-muted-foreground/30 text-4xl font-bold">
            {product.model?.charAt(0) || '?'}
          </div>
        )}
      </div>
      <div className="p-3 flex flex-col gap-1.5 flex-1">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-sm group-hover:text-primary leading-tight">
            {product.title || product.model}
          </h3>
          {badge ? (
            <Badge variant="default" className="shrink-0 text-[10px] px-1.5 py-0">
              {badge}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">{product.model}</p>
        <div className="mt-auto pt-2 flex flex-col gap-1 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="truncate text-primary">{product.manufacturer}</span>
            {product.pricedOnEachQuote ? (
              <span className="shrink-0 text-muted-foreground">Priced on each quote</span>
            ) : (
              <span className="font-medium">{formatMoney(product.price)}</span>
            )}
          </div>
          {product.boughtThrough ? (
            <span className="truncate text-muted-foreground">
              Bought through {product.boughtThrough}
            </span>
          ) : null}
        </div>
      </div>
    </>
  )

  if (onSelect) {
    return (
      <button
        type="button"
        disabled={disabled}
        onClick={() => onSelect(product)}
        className="group rounded-lg border bg-card transition-shadow hover:shadow-md overflow-hidden flex flex-col text-left disabled:cursor-wait disabled:opacity-60 disabled:hover:shadow-none"
      >
        {content}
      </button>
    )
  }

  return (
    <Link
      to="/products/$productId"
      params={{ productId: product.uuid }}
      className="group rounded-lg border bg-card transition-shadow hover:shadow-md overflow-hidden flex flex-col"
    >
      {content}
    </Link>
  )
}

export function ProductSearchResultCard({
  product,
  onSelect,
  disabled = false,
}: {
  product: ShopifySearchProduct
  onSelect?: (product: PickedProduct) => void
  disabled?: boolean
}) {
  return (
    <ProductPickCard
      product={pickedFromStorefront(product)}
      badge="Shopify"
      onSelect={onSelect}
      disabled={disabled}
    />
  )
}

const QUOTE_ONLY_PAGE_SIZE = 12
const QUOTE_ONLY_MAX = 96

// Products tracked for quoting that the storefront search doesn't show (USA-1109).
function QuoteOnlyResults({
  query,
  onSelect,
  disabled,
}: {
  query: string
  onSelect?: (product: PickedProduct) => void
  disabled: boolean
}) {
  const [limit, setLimit] = useState(QUOTE_ONLY_PAGE_SIZE)
  const quotable = useQuotableProducts(query, limit)
  const products = quotable.data?.items ?? []
  const total = quotable.data?.total ?? products.length

  if (quotable.isError) {
    return <p className="text-sm text-destructive">Quote-only products could not be searched.</p>
  }
  if (!products.length) return null
  return (
    <section aria-labelledby="quote-only-products" className="space-y-3">
      <div className="flex items-baseline justify-between gap-3">
        <h2 id="quote-only-products" className="text-sm font-semibold">
          Quote only
        </h2>
        <span className="text-xs text-muted-foreground">
          {formatProductSearchCount(total)} matching
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
        {products.map((product) => (
          <ProductPickCard
            key={product.uuid}
            product={pickedFromQuotable(product)}
            onSelect={onSelect}
            disabled={disabled}
          />
        ))}
      </div>
      {total > products.length && limit < QUOTE_ONLY_MAX ? (
        <Button
          variant="outline"
          size="sm"
          disabled={quotable.isFetching}
          onClick={() => setLimit((current) => current + QUOTE_ONLY_PAGE_SIZE)}
        >
          Show more
        </Button>
      ) : null}
    </section>
  )
}

export function ProductGridSkeleton({ count = 9 }: { count?: number }) {
  return (
    <div
      className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4"
      aria-label="Loading products"
    >
      {Array.from({ length: count }).map((_, idx) => (
        <Card key={idx} className="overflow-hidden">
          <Skeleton className="aspect-square w-full rounded-none" />
          <CardContent className="space-y-2 p-3">
            <div className="flex items-start justify-between gap-2">
              <Skeleton className="h-4 w-2/3" />
              <Skeleton className="h-4 w-12" />
            </div>
            <Skeleton className="h-3 w-1/2" />
            <div className="flex items-center justify-between gap-2 pt-5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-14" />
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  )
}

function ProductsPagination({
  page,
  totalPages,
  canGoNext,
  onPageChange,
}: {
  page: number
  totalPages?: number | null
  canGoNext: boolean
  onPageChange: (page: number) => void
}) {
  const exactTotalPages = totalPages != null ? Math.max(1, totalPages) : null
  const pageItems = exactTotalPages ? buildCompactPageItems(page, exactTotalPages) : [page]
  const pageSummary = exactTotalPages
    ? `Page ${formatProductSearchCount(page)} of ${formatProductSearchCount(exactTotalPages)}`
    : `Page ${formatProductSearchCount(page)}`

  return (
    <nav
      aria-label="Products pagination"
      className="flex flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-muted-foreground">{pageSummary}</p>
      <div className="flex flex-wrap items-center gap-1.5">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(Math.max(1, page - 1))}
          disabled={page <= 1}
          aria-label="Go to previous products page"
        >
          Previous
        </Button>
        {pageItems.map((item) =>
          typeof item === 'number' ? (
            <Button
              key={item}
              variant={item === page ? 'default' : 'outline'}
              size="sm"
              onClick={() => onPageChange(item)}
              aria-current={item === page ? 'page' : undefined}
              aria-label={`Go to products page ${item}`}
              className="min-w-9 px-2"
            >
              {formatProductSearchCount(item)}
            </Button>
          ) : (
            <span key={item} className="px-1.5 text-sm text-muted-foreground" aria-hidden="true">
              …
            </span>
          ),
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(page + 1)}
          disabled={!canGoNext}
          aria-label="Go to next products page"
        >
          Next
        </Button>
      </div>
    </nav>
  )
}

type ProductSearchPanelProps = {
  page: number
  onPageChange: (page: number) => void
  initialQuery?: string
  initialPerPage?: number
  initialSort?: ProductSort
  initialPriceMin?: string
  initialPriceMax?: string
  onSearchStateChange?: (state: ProductSearchState) => void
  renderProduct?: (product: ShopifySearchProduct) => ReactNode
  onSelectProduct?: (product: PickedProduct) => void
  selectionDisabled?: boolean
  defaultResults: ReactNode
  title?: string
  description?: string
  showHeader?: boolean
}

export function ProductSearchPanel({
  page,
  onPageChange,
  initialQuery = '',
  initialPerPage = DEFAULT_PUBLIC_PER_PAGE,
  initialSort = 'featured',
  initialPriceMin,
  initialPriceMax,
  onSearchStateChange,
  renderProduct,
  onSelectProduct,
  selectionDisabled = false,
  defaultResults,
  title = 'Products',
  description = 'Search the Shopify catalog and refine with contextual product filters.',
  showHeader = true,
}: ProductSearchPanelProps) {
  const [search, setSearch] = useState(initialQuery)
  const [publicPerPage, setPublicPerPage] = useState(initialPerPage)
  const [productSort, setProductSort] = useState<ProductSort>(initialSort)
  const [multiSelections, setMultiSelections] = useState<MultiSelections>({})
  const [draftRangeSelections, setDraftRangeSelections] = useState<RangeSelections>(() => {
    const price = selectedRange(initialPriceMin, initialPriceMax)
    const ranges: RangeSelections = {}
    if (price) ranges.price = price
    return ranges
  })

  const debouncedSearch = useDebouncedValue(search)
  const debouncedDraftRangeSelections = useDebouncedValue(draftRangeSelections)
  const committedRangeSelections = debouncedDraftRangeSelections
  const contextFilterCount = activeFilterCount(multiSelections, draftRangeSelections)
  const hasContextFilters = hasActiveContextFilters(multiSelections, draftRangeSelections)
  const shouldUsePublicSearch = Boolean(debouncedSearch.trim() || hasContextFilters)

  const notifySearchStateChange = useEffectEvent((state: ProductSearchState) => {
    onSearchStateChange?.(state)
  })
  const pendingRangePageReset = useRef<RangeSelections | null>(null)
  const pendingSortPageReset = useRef<ProductSort | null>(null)

  useEffect(() => {
    setSearch(initialQuery)
  }, [initialQuery])

  useEffect(() => {
    setPublicPerPage(initialPerPage)
  }, [initialPerPage])

  useEffect(() => {
    setProductSort(initialSort)
  }, [initialSort])

  useEffect(() => {
    setDraftRangeSelections((previous) => {
      const current = previous.price ?? {}
      if (current.min === initialPriceMin && current.max === initialPriceMax) return previous
      const next = { ...previous }
      const price = selectedRange(initialPriceMin, initialPriceMax)
      if (price) {
        next.price = price
      } else {
        delete next.price
      }
      return next
    })
  }, [initialPriceMin, initialPriceMax])

  useEffect(() => {
    const rangeReset = pendingRangePageReset.current === committedRangeSelections
    const sortReset = pendingSortPageReset.current === productSort
    const resetPage = rangeReset || sortReset
    if (rangeReset) pendingRangePageReset.current = null
    if (sortReset) pendingSortPageReset.current = null
    notifySearchStateChange({
      query: debouncedSearch.trim(),
      perPage: publicPerPage,
      sort: productSort,
      rangeSelections: committedRangeSelections,
      resetPage,
    })
  }, [debouncedSearch, publicPerPage, productSort, committedRangeSelections])

  const filterOptions = useShopifyFilterOptions(
    debouncedSearch,
    multiSelections,
    committedRangeSelections,
  )
  const publicSearch = useShopifySearch(
    debouncedSearch,
    multiSelections,
    committedRangeSelections,
    page,
    publicPerPage,
    productSort,
  )
  const filterGroups: ShopifyFilterGroup[] = useMemo(() => {
    const facets = [filterOptions.data?.price, ...(filterOptions.data?.facets ?? [])]
    if (!filterOptions.data) return []
    return orderFilterGroups(facets.filter(isRenderableFilterGroup))
  }, [filterOptions.data])
  const publicTotal = publicSearch.data?.total
  const publicPage = publicSearch.data?.page ?? page
  const publicTotalPages = publicSearch.data?.total_pages
  const publicProducts = publicSearch.data?.products ?? []
  const publicCanGoNext =
    publicTotalPages != null
      ? publicPage < publicTotalPages
      : publicProducts.length >= publicPerPage

  const toggleContextOption = (key: string, value: string) => {
    setMultiSelections((prev) => {
      const current = prev[key] ?? []
      const nextValues = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value]
      return { ...prev, [key]: nextValues }
    })
    onPageChange(1)
  }

  const updateRange = (key: string, bound: 'min' | 'max', value: string) => {
    const sanitized = sanitizeRangeInput(value)
    const next = {
      ...draftRangeSelections,
      [key]: { ...(draftRangeSelections[key] ?? {}), [bound]: sanitized },
    }
    pendingRangePageReset.current = next
    setDraftRangeSelections(next)
  }

  const clearContextGroup = (key: string) => {
    setMultiSelections((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    setDraftRangeSelections((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
    onPageChange(1)
  }

  const clearContextFilters = () => {
    setMultiSelections({})
    setDraftRangeSelections({})
    onPageChange(1)
  }

  return (
    <>
      {showHeader ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          {contextFilterCount > 0 && (
            <Badge variant="secondary" className="w-fit">
              {contextFilterCount} active contextual filter
              {contextFilterCount === 1 ? '' : 's'}
            </Badge>
          )}
        </div>
      ) : null}

      <div className="sticky top-0 z-30 flex flex-col gap-3 rounded-lg border bg-background/95 p-3 shadow-sm backdrop-blur md:flex-row md:flex-wrap lg:ml-[344px]">
        <div className="relative flex-1 min-w-0 md:min-w-[260px]">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <SearchInput
            placeholder="Search products, SKUs, brands, specs..."
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              onPageChange(1)
            }}
            className="pl-9"
          />
        </div>
      </div>

      {shouldUsePublicSearch && (
        <div className="flex flex-col gap-3 rounded-lg border bg-card px-3 py-2.5 sm:flex-row sm:items-center sm:justify-between lg:ml-[344px]">
          <p className="text-sm font-medium" aria-live="polite">
            {publicSearch.isLoading
              ? 'Finding matching products…'
              : formatProductResultsCopy(publicTotal)}
          </p>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Sort</span>
              <Select
                value={productSort}
                onValueChange={(value) => {
                  const nextSort = value as ProductSort
                  pendingSortPageReset.current = nextSort
                  setProductSort(nextSort)
                }}
              >
                <SelectTrigger className="w-[178px]" aria-label="Sort products">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PRODUCT_SORT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">Per page</span>
              <Select
                value={String(publicPerPage)}
                onValueChange={(value) => {
                  setPublicPerPage(Number(value))
                  onPageChange(1)
                }}
              >
                <SelectTrigger className="w-[76px]" aria-label="Per page">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PUBLIC_PER_PAGE_OPTIONS.map((value) => (
                    <SelectItem key={value} value={String(value)}>
                      {value}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
      )}

      <div className="grid gap-6 lg:grid-cols-[320px_minmax(0,1fr)]">
        <FilterPanel
          groups={filterGroups}
          isLoading={filterOptions.isLoading && !filterOptions.data}
          isUpdating={filterOptions.isFetching && !!filterOptions.data}
          isError={filterOptions.isError}
          total={
            shouldUsePublicSearch
              ? (publicTotal ?? filterOptions.data?.total)
              : filterOptions.data?.total
          }
          multiSelections={multiSelections}
          rangeSelections={draftRangeSelections}
          onToggleOption={toggleContextOption}
          onRangeChange={updateRange}
          onClearGroup={clearContextGroup}
          onClearAll={clearContextFilters}
        />

        <div className="min-w-0 space-y-4">
          {debouncedSearch.trim() && !hasContextFilters ? (
            <QuoteOnlyResults
              // A new search starts again at the first page of results.
              key={debouncedSearch.trim()}
              query={debouncedSearch.trim()}
              onSelect={onSelectProduct}
              disabled={selectionDisabled}
            />
          ) : null}
          {shouldUsePublicSearch ? (
            publicSearch.isLoading ? (
              <ProductGridSkeleton />
            ) : publicSearch.isError ? (
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-6 text-sm text-destructive">
                Product search failed. Clear filters or try a different query.
              </div>
            ) : publicProducts.length ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {publicProducts.map((product) => (
                  <div key={product.shopify_id} className="contents">
                    {renderProduct ? (
                      renderProduct(product)
                    ) : (
                      <ProductSearchResultCard
                        product={product}
                        onSelect={onSelectProduct}
                        disabled={selectionDisabled}
                      />
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="rounded-lg border bg-card p-8 text-center text-muted-foreground">
                <p>
                  No Shopify products found for “{debouncedSearch}”
                  {hasContextFilters ? ' with the selected filters.' : '.'}
                </p>
              </div>
            )
          ) : (
            defaultResults
          )}

          {shouldUsePublicSearch && !publicSearch.isLoading && !publicSearch.isError && (
            <ProductsPagination
              page={publicPage}
              totalPages={publicTotalPages}
              canGoNext={publicCanGoNext}
              onPageChange={onPageChange}
            />
          )}
        </div>
      </div>
    </>
  )
}
